import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { notify } from 'helpers/NotifyHelper';
import { storeUserInfo } from 'helpers/StorageHelper';
import { t } from 'i18next';
import { Preloader } from 'modules/admin/components/_common/Preloader';
import React, { Suspense, useCallback } from 'react';
import { Modal } from 'react-bootstrap';
import { updateProfileDetails, updateUserState } from 'store/auth/actions/AuthAction';
import { userInfoSelector, userProfileInfoSelector } from 'store/auth/selectors/authSelectors';
import {
  deliveryLocationModalOpenSelector,
  selectedDeliveryAddressSelector,
} from 'store/customer/selectors/CustomerGlobalSelectors';
import {
  setDeliveryLocationModalOpen,
  setSelectedDeliveryAddress,
} from 'store/customer/slices/CustomerGlobalSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import 'modules/customer/assets/scss/customerGeneralProfile.scss';

const CustomerAddressSection = React.lazy(
  () => import('../../subModules/CustomerProfile/GeneralProfile/Sections/CustomerAddressSection')
);

/**
 * "Select Delivery Location" popup. Mounted once in the header so every trigger
 * (the "Deliver to" block, the FFL name in the profile dropdown / mobile drawer)
 * opens the same modal instead of each rendering its own copy.
 */
export default function DeliveryLocationModal() {
  const dispatch = useAppDispatch();
  const showModal = useAppSelector(deliveryLocationModalOpenSelector);
  const profileInfo = useAppSelector(userProfileInfoSelector);
  const userInfo = useAppSelector(userInfoSelector);
  // same address object shown in the "Deliver to" block - used to hide the redundant
  // Select button on whichever address card is already active
  const activeAddress = useAppSelector(selectedDeliveryAddressSelector);
  const selectedAddressId = activeAddress?.id || activeAddress?._id || null;

  const handleCloseModal = useCallback(() => {
    dispatch(setDeliveryLocationModalOpen(false));
  }, [dispatch]);

  const handleDefaultAddressChange = useCallback(
    (address: any) => {
      dispatch(setSelectedDeliveryAddress(address));
    },
    [dispatch]
  );

  // Clears the active pick - "Deliver to" falls back to the same placeholder a
  // guest with no saved location sees, since that block's fallback text only
  // depends on there being no active address, not on login state.
  const handleUnselectAddress = useCallback(() => {
    dispatch(setSelectedDeliveryAddress(null));
    handleCloseModal();
  }, [dispatch, handleCloseModal]);

  const handleSelectAddress = useCallback(
    async (address: any) => {
      if (!profileInfo?.id) return;
      try {
        const profilePayload = {
          first_name: address.first_name || profileInfo.first_name || '',
          last_name: address.last_name || profileInfo.last_name || '',
          email: profileInfo.email || '',
          mobile: address.mobile || address.phone || profileInfo.mobile || '',
          mobile_code: address.mobile_code || profileInfo.mobile_code || '+1',
          add1: address.address_line_1 || address.add1 || '',
          country:
            typeof address.country === 'object'
              ? address.country?.id || address.country?._id
              : address.country || '',
          state:
            typeof address.state === 'object'
              ? address.state?.id || address.state?._id
              : address.state || '',
          city:
            typeof address.city === 'object'
              ? address.city?.id || address.city?._id
              : address.city || '',
          pincode:
            typeof address.pincode === 'object'
              ? address.pincode?.id || address.pincode?._id
              : address.pincode || '',
          zipcode:
            typeof address.pincode === 'object'
              ? address.pincode?.name
              : address.zip_code || address.pincode || '',
          selected_ffl: address.selected_ffl || address.ffl_id || null,
        };

        const response: any = await updateProfileDetails(profilePayload);
        if (response?.status) {
          notify.success(
            t('default_address_saved', { defaultValue: 'Default address saved successfully.' })
          );
          dispatch(setSelectedDeliveryAddress(address));
          const userData = {
            ...userInfo,
            user: {
              ...profileInfo,
              ...(response.data?.user || response.data),
              selected_ffl: profilePayload.selected_ffl,
              selected_address_id: address.id || address._id,
            },
          };
          storeUserInfo(userData);
          updateUserState(userData);
          handleCloseModal();
        } else {
          notify.error(response?.message || t('something_went_wrong'));
        }
      } catch (error) {
        console.error(error);
        notify.error(t('something_went_wrong'));
      }
    },
    [profileInfo, userInfo, dispatch, handleCloseModal]
  );

  if (!showModal) return null;

  return (
    <Modal
      show={showModal}
      onHide={handleCloseModal}
      scrollable
      centered
      className="custom-address-modal delivery-location-modal"
      size="xl"
    >
      <Modal.Header className="custom-address-modal__header">
        <h2>{t('select_delivery_location', { defaultValue: 'Select Delivery Location' })}</h2>
        <button className="close-btn" onClick={handleCloseModal} aria-label="Close modal">
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </Modal.Header>
      <Modal.Body className="custom-address-modal__body p-4">
        <Suspense fallback={<Preloader loading={true} />}>
          <div className="general-profile-main bg-transparent p-0 shadow-none">
            <div className="general-profile-section">
              <div className="profile-card-wrapper bg-transparent border-0">
                <CustomerAddressSection
                  onSelectAddress={handleSelectAddress}
                  onDefaultAddressChange={handleDefaultAddressChange}
                  onUnselectAddress={handleUnselectAddress}
                  selectedAddressId={selectedAddressId}
                />
              </div>
            </div>
          </div>
        </Suspense>
      </Modal.Body>
    </Modal>
  );
}
